import {
  Scene,
  FreeCamera,
  Vector3,
  Mesh,
  MeshBuilder,
  Ray,
  PickingInfo,
  AbstractMesh,
} from "@babylonjs/core";
import {
  Vec2,
  inputToWorldWish,
  accelerate,
  applyFriction,
  integrateVertical,
  conveyorGrabsFeet,
  ConveyorSurface,
} from "./controllerMath.js";

export interface ConveyorVolume {
  min: Vector3;
  max: Vector3;
  velocity: Vector3;
  /** When set, the volume only grabs a player whose feet are within a
   *  narrow band of this sloped surface, so people standing under or
   *  above the ramp are unaffected. */
  surface?: ConveyorSurface;
}

export interface MoveInput {
  axisX: number;   // strafe, right-positive, [-1, 1]
  axisY: number;   // forward-positive, [-1, 1]
  sprint: boolean;
  jump: boolean;   // edge-consumed: reset after use
}

export interface ControllerOpts {
  walkSpeed: number;
  sprintMultiplier: number;
  accel: number;
  friction: number;
  gravity: number;
  jumpSpeed: number;
  eyeHeight: number;
  stepHeight: number;
}

const DEFAULTS: ControllerOpts = {
  walkSpeed: 5.4,
  // One speed only: nobody runs through a mall. Kept as an option so the
  // input plumbing stays, but sprint is a no-op by default.
  sprintMultiplier: 1.0,
  accel: 30,
  friction: 24,
  gravity: 18,
  jumpSpeed: 5.6,
  eyeHeight: 1.7,
  stepHeight: 0.35,
};

/**
 * Grounded character controller. An invisible collider capsule-box owns the
 * physical position (Babylon collision ellipsoid + moveWithCollisions); the
 * camera is glued to its head every frame. There is no reachable floating
 * state: gravity always applies, jump only fires when grounded.
 */
export class CharacterController {
  readonly collider: Mesh;
  private readonly scene: Scene;
  private readonly camera: FreeCamera;
  private readonly opts: ControllerOpts;
  private velocity: Vec2 = { x: 0, z: 0 };
  private vy: number = 0;
  private grounded: boolean = false;
  private input: MoveInput = { axisX: 0, axisY: 0, sprint: false, jump: false };
  private conveyors: ConveyorVolume[] = [];
  private readonly downRay: Ray = new Ray(Vector3.Zero(), Vector3.Down(), 1.2);

  constructor(scene: Scene, camera: FreeCamera, opts?: Partial<ControllerOpts>) {
    this.scene = scene;
    this.camera = camera;
    this.opts = { ...DEFAULTS, ...opts };

    this.collider = MeshBuilder.CreateBox(
      "player-collider",
      { width: 0.6, height: 1.8, depth: 0.6 },
      scene
    );
    this.collider.isVisible = false;
    this.collider.isPickable = false;
    this.collider.checkCollisions = false; // it moves; it is not an obstacle
    this.collider.ellipsoid = new Vector3(0.45, 0.9, 0.45);
    this.collider.position = new Vector3(0, 0.9, 0);

    // The camera stops being a physics object: the controller owns motion.
    this.camera.applyGravity = false;
    this.camera.checkCollisions = false;
    this.camera.inertia = 0.3;
  }

  setInput(partial: Partial<MoveInput>): void {
    this.input = { ...this.input, ...partial };
  }

  /** Place the player's feet at (x, groundY, z). A small lift prevents
   *  spawning a hair below raised floors (they sit at y=0.01) and falling
   *  through; gravity settles the player onto the surface next frame. */
  teleport(position: Vector3, yaw?: number): void {
    this.collider.position.set(position.x, position.y + 0.9 + 0.1, position.z);
    this.velocity = { x: 0, z: 0 };
    this.vy = 0;
    if (yaw !== undefined) {
      this.camera.rotation.y = yaw;
    }
    this.syncCamera();
  }

  /** Register conveyor volumes (escalators); replaces the previous set. */
  setConveyors(volumes: ConveyorVolume[]): void {
    this.conveyors = volumes;
  }

  private conveyorVelocity(): Vector3 | null {
    const p: Vector3 = this.collider.position;
    const feetY: number = p.y - 0.9;
    for (const c of this.conveyors) {
      if (conveyorGrabsFeet(p.x, feetY, p.z, c.min, c.max, c.surface)) {
        return c.velocity;
      }
    }
    return null;
  }

  isGrounded(): boolean {
    return this.grounded;
  }

  /** Feet position (world space). */
  feet(): Vector3 {
    return new Vector3(
      this.collider.position.x,
      this.collider.position.y - 0.9,
      this.collider.position.z
    );
  }

  update(dtSeconds: number): void {
    const dt: number = Math.min(dtSeconds, 0.05); // clamp long frames
    const yaw: number = this.camera.rotation.y;

    const hasInput: boolean = this.input.axisX !== 0 || this.input.axisY !== 0;
    if (hasInput) {
      const wish: Vec2 = inputToWorldWish(this.input.axisX, this.input.axisY, yaw);
      const maxSpeed: number =
        this.opts.walkSpeed * (this.input.sprint ? this.opts.sprintMultiplier : 1);
      this.velocity = accelerate(this.velocity, wish, this.opts.accel, maxSpeed, dt);
    } else {
      this.velocity = applyFriction(this.velocity, this.opts.friction * this.opts.walkSpeed, dt);
    }

    if (this.input.jump && this.grounded) {
      this.vy = this.opts.jumpSpeed;
      this.grounded = false;
    }
    this.input.jump = false;

    const vertical = integrateVertical(this.vy, this.opts.gravity, dt);
    this.vy = vertical.vy;

    const before: Vector3 = this.collider.position.clone();
    const horizontal: Vector3 = new Vector3(this.velocity.x * dt, 0, this.velocity.z * dt);
    const conveyor: Vector3 | null = this.conveyorVelocity();
    if (conveyor !== null) {
      horizontal.addInPlace(conveyor.scale(dt));
    }
    this.collider.moveWithCollisions(horizontal);
    // The collision ellipsoid slides up faces it pushes against; unchecked,
    // that was the old "floating" bug. While grounded, allow only as much
    // rise as a steep ramp explains (escalators/slopes at <= ~50 deg);
    // airborne, horizontal moves gain no height at all.
    const movedX: number = this.collider.position.x - before.x;
    const movedZ: number = this.collider.position.z - before.z;
    const hDist: number = Math.hypot(movedX, movedZ);
    const maxRise: number = this.grounded ? hDist * 1.2 + 0.02 : 0;
    if (this.collider.position.y > before.y + maxRise) {
      this.collider.position.y = before.y + maxRise;
    }

    // Step-over: if we were mostly blocked horizontally, probe from a small
    // lift. Keep whichever result (slide vs step) makes more progress; the
    // step probe must never leave us higher than one step above where we began.
    const slide: Vector3 = this.collider.position.clone();
    const slideSq: number = (slide.x - before.x) ** 2 + (slide.z - before.z) ** 2;
    const intendedSq: number = horizontal.x ** 2 + horizontal.z ** 2;
    if (this.grounded && intendedSq > 1e-8 && slideSq < intendedSq * 0.25) {
      this.collider.position.copyFrom(before);
      this.collider.moveWithCollisions(new Vector3(0, this.opts.stepHeight, 0));
      this.collider.moveWithCollisions(horizontal);
      // The probe's horizontal move can also slide up; clamp to the lift height
      // so nothing taller than stepHeight is ever climbable.
      this.collider.position.y = Math.min(this.collider.position.y, before.y + this.opts.stepHeight);
      this.collider.moveWithCollisions(new Vector3(0, -this.opts.stepHeight, 0));
      const stepped: Vector3 = this.collider.position;
      const stepSq: number = (stepped.x - before.x) ** 2 + (stepped.z - before.z) ** 2;
      if (stepSq <= slideSq || stepped.y > before.y + this.opts.stepHeight) {
        this.collider.position.copyFrom(slide); // step didn't help; keep the slide
      }
    }

    // Vertical move + ground detection. Grounded when the downward sweep is
    // absorbed by a collision (robust on edges) or the ground ray hits.
    const beforeVerticalY: number = this.collider.position.y;
    this.collider.moveWithCollisions(new Vector3(0, vertical.dy, 0));
    const actualDy: number = this.collider.position.y - beforeVerticalY;
    const fallBlocked: boolean = vertical.dy < -1e-6 && actualDy > vertical.dy * 0.5;
    this.grounded = fallBlocked || this.checkGrounded();
    if (this.grounded && this.vy < 0) {
      this.vy = 0;
    }

    // Safety net: never fall through the world. Respawn at the atrium entry
    // (NOT the origin — the fountain lives there).
    if (this.collider.position.y < -20) {
      this.collider.position.set(0, 2, -20);
      this.vy = 0;
    }

    this.syncCamera();
  }

  private checkGrounded(): boolean {
    this.downRay.origin.copyFrom(this.collider.position);
    this.downRay.length = 0.9 + 0.15;
    const hit: PickingInfo | null = this.scene.pickWithRay(
      this.downRay,
      (m: AbstractMesh) => m.checkCollisions && m !== this.collider
    );
    return hit !== null && hit.hit;
  }

  private syncCamera(): void {
    this.camera.position.set(
      this.collider.position.x,
      this.collider.position.y - 0.9 + this.opts.eyeHeight,
      this.collider.position.z
    );
  }
}
